import { ProductSalesModel } from 'api-hooks/product/sales';
import { ActivitySaleModel } from 'api-hooks/sale/activity';
import { ApprovalItemWorkflowEnum } from 'api-hooks/sale/common';
import { CruiseSaleModel } from 'api-hooks/sale/cruise';
import { HotelSaleModel } from 'api-hooks/sale/hotel';
import { TicketSaleModel } from 'api-hooks/sale/ticket-sale';
import { TravelDocumentSalesModel } from 'api-hooks/sale/travel-document';
import { TourSalesModel } from 'api-hooks/tour/tour-sale';
import { distinct } from 'common/utils/iterable';
import { capitalize, generateId } from 'common/utils/string';
import { generateUniqueAirlineItem } from 'legacy-modules/order/ticket-order/hooks/use-get-default-values';
import {
  guestNameTransformer,
  guestPreferencesTransformer,
  preferencesTransformer,
} from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';

import defaultValueProject from '../project/default-value';

export default function defaultValuesSale(
  data:
    | TicketSaleModel
    | HotelSaleModel
    | CruiseSaleModel
    | ActivitySaleModel
    | TravelDocumentSalesModel
    | TourSalesModel
    | ProductSalesModel,
  transactionType?: string,
  workflow?: string | null,
) {
  const defaultValues: any = {
    transactionType,
    number: data.invoiceNumber,
    transactionAt: data.transactionAt,
    admin: data.admin.name,
    customer: `${data.customer.name}${
      data.customer.alias ? ` (${data.customer.alias})` : ''
    } - ${data.customer.phone}`,
    customerBilling: data.customerBillingAddress?.address ?? '',
    operator: data.operator?.name,
    salesman: data.salesman?.name,
    address: data.customer.address,
    phone: data.customer.phone,
    status: data.status,
    code: data.code,
    description: data.description,
    waitingId: data.waitingId,
    id: data.id,
  };

  if (workflow === ApprovalItemWorkflowEnum.sale_project) {
    return {
      ...defaultValues,
      workflow,
      project: data.project && defaultValueProject(data.project),
    };
  }

  if (transactionType) {
    if (transactionType === 'activity') {
      const activitySale = data as ActivitySaleModel;
      return {
        ...defaultValues,

        activitySales: (activitySale.activitySales || []).map((hs) => {
          const activityCustomIds: string[] = hs.isGroupedPrice
            ? (hs.activitySaleGuests[0].activitySaleGuestDetails || []).map(
                () => generateId(),
              )
            : [];

          return {
            id: hs.id,
            isGroupedPrice: hs.isGroupedPrice,
            activitySaleGuests: (hs.activitySaleGuests || []).map((guest) => ({
              id: guest.id,
              guestId: guest.guest.id,
              guestName: `${capitalize(guest.guest.title)} ${guest.guest.name}`,
              activitySaleGuestDetails: (
                guest.activitySaleGuestDetails || []
              ).map((asgd, idx) => ({
                id: asgd.id,
                customId: activityCustomIds[idx],
                price: asgd.price,
                description: asgd.description,
                activityId: asgd.activity.id,
                activityName: asgd.activity.name,
                type: capitalize(asgd.activity.type),
                date: asgd.date,
                qty: asgd.qty,
                totalPrice: asgd.totalPrice,
              })),
              guestPreferences: [
                ...guest.guest.preferences,
                ...guest.guest.preferenceStats,
              ].map((item: any) => ({
                id: item.id,
                label: item.count ? `${item.name} (X${item.count})` : item.name,
              })),
              preferences: (guest.preferences || []).map((preference) => ({
                id: preference.id,
                label: preference.name,
              })),
            })),
            activitySaleAddOns: (hs.activitySaleAddOns || []).map((addons) => ({
              id: addons.id,
              guestId: addons.guest.id,
              guestName: `${capitalize(addons.guest.title)} ${
                addons.guest.name
              }`,
              price: addons.price,
              description: addons.description,
            })),
          };
        }),
      };
    }
    if (transactionType === 'cruise') {
      const cruiseSale = data as CruiseSaleModel;

      return {
        ...defaultValues,

        cruiseSales: (cruiseSale.cruiseSales || []).map((hs) => ({
          id: hs.id,
          bookingCode: hs.bookingCode,
          cruiseId: hs.cruise.id,
          cruiseName: hs.cruise.name,
          cruiseShipId: hs.cruiseShip?.id,
          cruiseShipName: hs.cruiseShip?.name,
          isGroupedPrice: hs.isGroupedPrice,
          startedAt: hs.startedAt,
          endedAt: hs.endedAt,
          reminder: hs.reminder,
          reminderDate: hs.reminderDate,
          reminderDescription: hs.reminderDescription,
          route: hs.route,
          cruiseSaleAddOns: (hs.cruiseSaleAddOns || []).map((addon) => ({
            id: addon.id,
            description: addon.description,
            price: addon.price,
            guestId: addon.guest.id,
            guestName: `${capitalize(addon.guest.title)} ${addon.guest.name}`,
          })),
          cruiseSaleGuests: (hs.cruiseSaleGuests || []).map((guest) => ({
            id: guest.id,
            guestId: guest.guest.id,
            guestName: `${capitalize(guest.guest.title)} ${guest.guest.name}`,
            guestPreferences: [
              ...guest.guest.preferences,
              ...guest.guest.preferenceStats,
            ].map((item: any) => ({
              id: item.id,
              label: item.count ? `${item.name} (X${item.count})` : item.name,
            })),
            preferences: (guest.preferences || []).map((preference) => ({
              id: preference.id,
              label: preference.name,
            })),
          })),
          cruiseSaleDetails: (hs.cruiseSaleDetails || []).map((detail) => {
            const guest = hs.cruiseSaleGuests.find(
              (csg) => csg.id === detail.cruiseSaleGuestId,
            )?.guest;
            return {
              id: detail.id,
              guestId: guest?.id || '',
              guestName: hs.isGroupedPrice
                ? ''
                : `${capitalize(guest?.title)} ${guest?.name}`,
              basicFare: detail.basicFare,
              portCharge: detail.portCharge,
              tax: detail.tax,
              tipping: detail.tipping,
              description: detail.description,
              cruiseRoomId: detail.cruiseRoom.id,
              cruiseRoomName: `${detail.cruiseRoom.type} (${detail.cruiseRoom.size})`,
              cruiseRoomNumber: detail.cruiseRoomNumber,
              deck: detail.deck,
            };
          }),
        })),
      };
    }
    if (transactionType === 'flight') {
      const flightSale = data as TicketSaleModel;

      const flightSales = (flightSale.flightSales || []).map((schedule) => {
        const flightSaleAirlines = distinct(schedule.flightSaleGuests, (fsg) =>
          generateUniqueAirlineItem(fsg),
        ).map((fsg) => {
          return {
            detail: fsg.detail,
            preferenceId: fsg.preference?.id || '',
            preferenceName: fsg.preference?.name || '',
            route: fsg.route,
            time: fsg.time,
            date: fsg.date,
            airlineId: fsg.airline.id,
            airlineName: fsg.airline.name,
            price: fsg.price,
            checkInStatus: fsg.checkInStatus,
          };
        });

        const flightSaleGuests = distinct(
          schedule.flightSaleGuests,
          (fsg) => fsg.guest.id,
        ).map((fsg) => {
          return {
            guestId: fsg.guest.id,
            guestName: guestNameTransformer(fsg.guest),
            ticket: fsg.ticket,
            guestPreferences: guestPreferencesTransformer(fsg.guest),
            preferences: preferencesTransformer(fsg.preferences),
          };
        });
        return {
          id: schedule.id,
          bookingCode: schedule.bookingCode,
          flightSaleAddOns: (schedule.flightSaleAddOns || []).map((addon) => ({
            id: addon.id,
            route: addon.route,
            description: addon.description || '',
            price: addon.price,
            guestId: addon.guest.id,
            guestName: `${capitalize(addon.guest.title)} ${addon.guest.name}`,
          })),
          flightSaleGuests,
          flightSaleAirlines,
        };
      });
      return {
        ...defaultValues,
        flightSales,
      };
    }
    if (transactionType === 'hotel') {
      const hotelSale = data as HotelSaleModel;
      return {
        ...defaultValues,

        hotelSales: (hotelSale.hotelSales || []).map((hs) => ({
          id: hs.id,
          bookingCode: hs.bookingCode,
          hotelId: hs.hotel.id,
          hotelName: hs.hotel.name,
          hotelPhone: hs.hotel.phone,
          hotelEmail: hs.hotel.email,
          hotelAddress: hs.hotel.address,
          hotelSaleAddOns: (hs.hotelSaleAddOns || []).map((addon) => ({
            id: addon.id,
            description: addon.description,
            price: addon.price,
            guestId: addon.guest.id,
            guestName: `${capitalize(addon.guest.title)} ${addon.guest.name}`,
          })),
          hotelSaleHotels: (hs.hotelSaleHotels || []).map((hsh) => ({
            id: hsh.id,
            checkIn: hsh.checkIn,
            checkOut: hsh.checkOut,
            detail: hsh.detail,
            night: hsh.night,
            room: hsh.room,
            price: hsh.price,
            hotelRoomId: hsh.hotelRoom.id,
            hotelRoomName: hsh.hotelRoom.type,
            checkInStatus: hsh.checkInStatus,
            hotelConfirmationCode: hsh.hotelConfirmationCode,
          })),
          hotelSaleGuests: (hs.hotelSaleGuests || []).map((guest) => ({
            id: guest.id,
            guestId: guest.guest.id,
            guestName: `${capitalize(guest.guest.title)} ${guest.guest.name}`,
            guestPreferences: [
              ...guest.guest.preferences,
              ...guest.guest.preferenceStats,
            ].map((item: any) => ({
              id: item.id,
              label: item.count ? `${item.name} (X${item.count})` : item.name,
            })),

            preferences: (guest.preferences || []).map((preference) => ({
              id: preference.id,
              label: preference.name,
            })),
          })),
          cityTax: hs.cityTax,
          isCityTax: hs.isCityTax,
          isByPax: hs.isByPax,
          defaultCheckInOut: {
            in: '',
            out: '',
          },
          // remark: hs.remark,
        })),
      };
    }
    if (transactionType === 'product') {
      const productSale = data as ProductSalesModel;

      return {
        ...defaultValues,
        productSales: (productSale.productSales || []).map((productSale) => ({
          productVariantId: productSale.productVariant.id,
          productVariantName: productSale.completeName,
          price: productSale.price,
          qty: productSale.qty,
          description: productSale.description,
          stock: productSale.productVariant.stock,
        })),
      };
    }
    if (transactionType === 'document') {
      const documentSale = data as TravelDocumentSalesModel;
      return {
        ...defaultValues,
        documentSales: (documentSale.documentSales || []).map(
          (documentSale) => ({
            documentReceiveDate: documentSale.documentReceiveDate,
            reminder: documentSale.reminder,
            reminderDate: documentSale.reminderDate,
            reminderDescription: documentSale.reminderDescription,
            documentType: capitalize(documentSale.documentType),
            visaDocument:
              documentSale.visaDocuments
                ?.map((vd) => [vd.code, vd.name].join(' - '))
                .join(', ') || '',
            documentSaleAddOns: (documentSale.documentSaleAddOns || []).map(
              (addons) => ({
                guestId: addons.guest.id,
                guestName: `${capitalize(addons.guest.title)} ${
                  addons.guest.name
                }`,
                description: addons.description,
                price: addons.price,
              }),
            ),
            documentSaleGuests: (documentSale.documentSaleGuests || []).map(
              (guest) => {
                return {
                  guestId: guest.guest.id,
                  guestName: `${capitalize(guest.guest.title)} ${
                    guest.guest.name
                  }`,
                  guestPreferences: [
                    ...guest.guest.preferences,
                    ...guest.guest.preferenceStats,
                  ].map((item: any) => ({
                    id: item.id,
                    label: item.count
                      ? `${item.name} (X${item.count})`
                      : item.name,
                  })),
                  preferences: (guest.preferences || []).map((preference) => ({
                    id: preference.id,
                    label: preference.name,
                    preferenceId: preference.id,
                  })),
                  details: Array.isArray(guest.details)
                    ? guest.details.map((detail) => ({
                        id: detail.id,
                        description: detail.description,
                        price: detail.price,
                        appointmentAt: detail.appointmentAt || new Date(),
                        biometric: detail.biometric,
                        startAt: detail.startAt,
                        endAt: detail.endAt,
                        days: detail.days,
                        package: detail.package,
                        company: detail.company,
                        coverArea: detail.coverArea,
                        visaDocumentId: detail.visaDocument?.id,
                        label: detail.visaDocument?.name,
                        type: detail.type,
                      }))
                    : [
                        {
                          id: guest.details.id,
                          description: guest.details.description,
                          price: guest.details.price,
                          type: guest.details.type,
                        },
                      ],
                };
              },
            ),
          }),
        ),
      };
    }
    if (transactionType === 'tour') {
      const tourSale = data as TourSalesModel;
      return {
        ...defaultValues,
        tourSales: (tourSale.tourSales || []).map((item) => ({
          id: item.id,

          tourSaleGuests: (item.tourSaleGuests || []).map((tourGuestItem) => ({
            id: tourGuestItem.id,
            guestId: tourGuestItem.guest.id,
            price: tourGuestItem.price,
            guestName: `${tourGuestItem.guest.title}. ${tourGuestItem.guest.name}`,
            groupNumber: tourGuestItem.groupNumber,
            bedType: capitalize(tourGuestItem.bedType),
            tourFlightName: tourGuestItem.tourFlight?.name,
            type: tourGuestItem.tourPrice?.type,
            tourPrice: tourGuestItem.tourPrice?.type,
            note: tourGuestItem.note,
            guestPreferences: [...tourGuestItem.guest.preferenceStats].map(
              (guestPref) => ({
                id: guestPref.id,
                label: guestPref.count
                  ? `${guestPref.name} (X${guestPref.count})`
                  : guestPref.name,
              }),
            ),
          })),
          tourSaleAddOns: (item.tourSaleAddOns || []).map((addOnsItem) => ({
            id: addOnsItem.id,
            guestId: addOnsItem.guest.id,
            description: addOnsItem.description,
            price: addOnsItem.price,
          })),
          tourSaleOtherPrices:
            (item.tourSaleOtherPrices || [])?.map((otherItem) => ({
              tourOtherPriceId: otherItem.tourOtherPrice.id,
              qty: otherItem.qty,
              total: otherItem.total,
              description: otherItem.description,
              price: otherItem.price,
            })) || [],
        })),
      };
    }
  }

  return defaultValues;
}
